import { collection,  onSnapshot ,Firestore , QuerySnapshot, getDocs ,getCountFromServer, doc, getDoc, query, DocumentData } from "firebase/firestore";
import {db, realtimeDb} from "./FirestoreDataLoader.tsx";
import { ref as databaseRef, onValue } from 'firebase/database';
import {useEffect, useState} from "react";

export const countUsersFromFirestore = async (): Promise<number> => {
  const snapshot = await getCountFromServer(collection(db, "utenti"));
  return snapshot.data().count;
};

export const sommaFileCaricati = async (): Promise<number> => {
  try {
    const utentiRef = collection(db, "utenti");
    const snapshot = await getDocs(utentiRef);

    let totale = 0;

    snapshot.forEach(doc => {
      const data = doc.data();
      const statistiche = data.statistiche_utente;
      const fileCaricati = statistiche?.fileCaricati ?? 0; // default 0 se mancante
      totale += fileCaricati;
    });

    return totale;
  } catch (error) {
    console.error("Errore nel calcolo della somma dei file caricati:", error);
    return 0;
  }
};

export interface DatiUtenteSemplificati {
  uid: string;
  email: string;
  userPhotoURL:string;
  tipo_account: string;
  data_registrazione:string;
  ultimoAccessoTimestamp: string;
  fileCaricati: number;
  fileCliente:[];
  tempoUtilizzoSecondi: number;
  statoInvito: string;
  fileRielaborati:number;
}

export const DatiTotUtenti = (db: Firestore,  callback: (dati: DatiUtenteSemplificati[]) => void): () => void => {
  const utentiRef = collection(db, "utenti"); 
  const rtdbUnsubscribes: Record<string, () => void> = {};
  // Cache persistente per combinare i dati (Firestore + RTDB)
  let userCache: Map<string, DatiUtenteSemplificati> = new Map();
    
  // Logica di formattazione data
  const formatDataRegistrazione = (timestamp: string | number): string => {
    try {
      const localDate = new Date(timestamp).toLocaleString(
          typeof navigator !== 'undefined' ? navigator.language : 'it-IT',
            {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: false, 
            }
      );
      return localDate; 
    } catch (e) {
      console.warn("Errore durante la formattazione della data:", e);
      return new Date(timestamp).toLocaleDateString();
    }
  };
    
  // Listener principale
  const unsubscribeFirestore = onSnapshot(utentiRef, (snapshot: QuerySnapshot<DocumentData>) => { 
        
  // Mappa temporanea 
  const currentFirestoreUsers: Map<string, DatiUtenteSemplificati> = new Map();

        // Pulisci i listener RTDB non più necessari 
        const activeUids = new Set(snapshot.docs.map(doc => doc.id));
        Object.keys(rtdbUnsubscribes).forEach(uid => {
            if (!activeUids.has(uid)) {
                rtdbUnsubscribes[uid](); // Esegui la pulizia RTDB
                delete rtdbUnsubscribes[uid];
            }
        });

        // Popola la cache con i dati statici
        snapshot.docs.forEach(doc => {
            const uid = doc.id;
            const data = doc.data() as DocumentData; 
            
            // Tenta di recuperare lo stato di presenza RTDB dalla cache precedente
            const cachedUser = userCache.get(uid);
            
            const dataRegistrazioneFormatted = data.data_registrazione 
                ? formatDataRegistrazione(data.data_registrazione) 
                : '';

            const dataUltimoAccessoTimestamp = cachedUser?.ultimoAccessoTimestamp ?
                cachedUser?.ultimoAccessoTimestamp == 'online' ? 
                'online':
                formatDataRegistrazione(cachedUser.ultimoAccessoTimestamp): 
                '';
            
            const userBaseData: DatiUtenteSemplificati = {
                uid: uid,
                email: data.email || '', 
                userPhotoURL: data.userPhotoURL || '',
                tipo_account: data.tipo_account || '',
                data_registrazione: dataRegistrazioneFormatted || '',
                ultimoAccessoTimestamp: dataUltimoAccessoTimestamp || '', 
                fileCaricati: data.statistiche_utente?.fileCaricati || 0,
                fileCliente: data.file_cliente ? data.file_cliente.length : [],
                tempoUtilizzoSecondi: data.statistiche_utente?.tempoUtilizzoSecondi || 0,
                statoInvito: data.statoInvito || '',
                fileRielaborati:  data.statistiche_utente?.fileCaricati-data.storico_file.length || 0,
            };
            
            currentFirestoreUsers.set(uid, userBaseData);

            if (!rtdbUnsubscribes[uid]) {
                const rtdbRef = databaseRef(realtimeDb, `presenza/${uid}`); 

                const unsubscribeRTDB = onValue(rtdbRef, (rtdbSnapshot) => {  
                    const rtdbData = rtdbSnapshot.val();
                    const presenceStatus = rtdbData?.ultimoAccessoTimestamp?.toString() || ''; 
                    const dataUltimoAccessoTimestamp = presenceStatus ?
                      presenceStatus == 'online' ? 
                      'online':
                      formatDataRegistrazione(presenceStatus): 
                      '';
                    
                    // Aggiorna la cache globale solo con il dato dinamico
                    if (userCache.has(uid)) {
                        const userToUpdate = userCache.get(uid)!;
                        userToUpdate.ultimoAccessoTimestamp = dataUltimoAccessoTimestamp;
                        userCache.set(uid, userToUpdate);

                        // Richiama lista aggiornata
                        callback(Array.from(userCache.values())); 
                    }
                });
                rtdbUnsubscribes[uid] = unsubscribeRTDB;
            }
        });
        
        // cache con dati appena letti da Firestore
        userCache = currentFirestoreUsers;
        callback(Array.from(userCache.values()));
    }, (error:any) => {
        console.error("Errore nel listener dei dati degli utenti:", error);
        throw new Error("Errore nel listener dei dati degli utenti: " + error.message);
    });

    // cleanup 
    return () => {
        unsubscribeFirestore(); 
        Object.values(rtdbUnsubscribes).forEach(unsubscribe => unsubscribe()); 
    };
};


//Conta il numero di file di un cliente che hanno lo stato "Elaborato".
const contaFileElaboratiCliente = async (uidCliente: string) => {
    const docRef = doc(db, "utenti", uidCliente);
    
    try {
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const datiCliente = docSnap.data() as DocumentData;

            const fileCliente = datiCliente.file_cliente || [];

            const conteggioInviati = fileCliente.filter(
                (file: any) => file.stato === "Elaborato"
            ).length;

            return conteggioInviati;
        } else {
            console.warn(`Documento utente non trovato per l'UID: ${uidCliente}`);
            return null;
        }
    } catch (error) {
        console.error("Errore nel conteggio dei file inviati:", error);
        return null; 
    }
};

export const useFileElaboratiCount = (uidCliente: string | undefined) => {
    const [count, setCount] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!uidCliente || uidCliente== '') return;

        setIsLoading(true);
        setCount(null); // Resetta all'inizio del fetch

        const fetchCount = async () => {
            try {
                // Chiama la tua funzione asincrona
                const result = await contaFileElaboratiCliente(uidCliente);
                setCount(result);
            } catch (error) {
                console.error("Errore nel fetch del conteggio file:", error);
                setCount(0); // Gestione errore
            } finally {
                setIsLoading(false);
            }
        };

        fetchCount();

    }, [uidCliente]);

    return { count, isLoading };
};

//Conta il numero di file di un cliente che hanno lo stato "Inviato".
const contaFileInviatiCliente = async (uidCliente: string) => {
    const docRef = doc(db, "utenti", uidCliente);
    
    try {
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const datiCliente = docSnap.data() as DocumentData;

            const fileCliente = datiCliente.file_cliente || [];

            const conteggioInviati = fileCliente.filter(
                (file: any) => file.stato === "Inviato" || file.stato === "Scaricato" 
            ).length;

            return conteggioInviati;
        } else {
            console.warn(`Documento utente non trovato per l'UID: ${uidCliente}`);
            return null;
        }
    } catch (error) {
        console.error("Errore nel conteggio dei file inviati:", error);
        return null; 
    }
};

export const useFileInviatiCount = (uidCliente: string | undefined) => {
    const [count, setCount] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!uidCliente || uidCliente== '') return;

        setIsLoading(true);
        setCount(null); // Resetta 

        const fetchCount = async () => {
            try {
                const result = await contaFileInviatiCliente(uidCliente);
                setCount(result);
            } catch (error) {
                console.error("Errore nel fetch del conteggio file:", error);
                setCount(0); // Gestione errore
            } finally {
                setIsLoading(false);
            }
        };

        fetchCount();

    }, [uidCliente]);

    return { count, isLoading };
};

// Crea un nuovo hook per il client-side aggregation
export const useAllClientFileCounts = (clienti_invitati: any[] | null) => {
    
    const [clientiData, setClientiData] = useState<any[]>([]); 
    const [isLoadingAll, setIsLoadingAll] = useState(true);
    
    useEffect(() => {
      if (!clienti_invitati) {
        setClientiData([]); 
        setIsLoadingAll(false);
        return;
      }
        
      setIsLoadingAll(true);

      const fetchAndSetData = async () => {
        const combinedData: any[] = [];
    
        if (!clienti_invitati) {
          setIsLoadingAll(false);
          return;
        }

        try {
          for (const datiCliente of clienti_invitati) {
            const uid = datiCliente.uid;
            
            const xmlCount = await contaFileElaboratiCliente(uid);
            const pdfCount = await contaFileInviatiCliente(uid);
            
            combinedData.push({
              uid: uid,
              ...datiCliente,
              file_caricati_count: datiCliente.file_cliente?.length,
              xml_completati_count: xmlCount,
              pdf_scaricabili_count: pdfCount,
            });
          }

          setClientiData(combinedData);
        
        } catch (error) {
          console.error("Errore nel recupero dei conteggi file:", error);
          setClientiData([]);
        } finally {
          setIsLoadingAll(false);
        }
      };
        fetchAndSetData();

    }, [clienti_invitati]); 

    return { clientiData, isLoadingAll };
};
